import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { EditRestrictionConfigDialog } from '@/components/admin/EditRestrictionConfigDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AlertTriangle, Database, Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Re-define the RestrictionConfig type locally to avoid import issues
interface RestrictionConfig {
  allowed_ehr_ids?: string[];
  blocked_ehr_ids?: string[];
  allowed_duration_categories?: ('short' | 'medium' | 'long' | 'other')[];
  allowed_condition_tags?: string[]; // Condition tag keys for specialty-specific slots
  blocked_all?: boolean;
  no_urgency_override?: boolean; // If true, urgency window does NOT bypass allowed_* restrictions
  patient_status?: 'new' | 'existing' | 'any';
  reason?: string;
}

export const Route = createFileRoute('/_protected/admin/restriction-configs')({
  component: RestrictionConfigsPage,
});

function RestrictionConfigsPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State for EHR selection only
  const [selectedEhrSystem, setSelectedEhrSystem] = useState<'imedicware'>('imedicware');

  // Filter state
  const [filterStatus, setFilterStatus] = useState<'all' | 'unknown' | 'configured'>('unknown');
  const [searchQuery, setSearchQuery] = useState('');

  // State for edit dialog
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    labelPattern: string;
    configId: string | null;
    currentConfig: RestrictionConfig | null;
  }>({
    isOpen: false,
    labelPattern: '',
    configId: null,
    currentConfig: null,
  });

  // Use current organization ID
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries - will be enabled when organization is loaded
  const allLabelsQuery = trpc.admin.getAllLabels.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  const appointmentTypesQuery = trpc.admin.getAppointmentTypes.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  const conditionTagsQuery = trpc.admin.getConditionTags.useQuery(
    { organizationId: currentOrganizationId || '' },
    { enabled: !!currentOrganizationId }
  );

  // tRPC mutations
  const updateRestrictionConfigMutation = trpc.admin.updateRestrictionConfig.useMutation({
    onSuccess: async () => {
      toast.success('Restriction configuration updated successfully');
      // Refetch queries to update the UI
      await allLabelsQuery.refetch();
      setEditDialog({ isOpen: false, labelPattern: '', configId: null, currentConfig: null });
    },
    onError: (error) => {
      toast.error(`Failed to update restriction config: ${error.message}`);
    },
  });

  const createRestrictionConfigMutation = trpc.admin.createRestrictionConfig.useMutation({
    onSuccess: async () => {
      toast.success('Restriction configuration created successfully');
      // Refetch queries to update the UI
      await allLabelsQuery.refetch();
      setEditDialog({ isOpen: false, labelPattern: '', configId: null, currentConfig: null });
    },
    onError: (error) => {
      toast.error(`Failed to create restriction config: ${error.message}`);
    },
  });

  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  const handleEditConfig = (
    labelPattern: string,
    configId: string | null = null,
    currentConfig: RestrictionConfig | null = null
  ) => {
    setEditDialog({
      isOpen: true,
      labelPattern,
      configId,
      currentConfig,
    });
  };

  const handleSaveConfig = (restrictionConfig: RestrictionConfig) => {
    if (!currentOrganizationId) {
      toast.error('Cannot save config without organization ID');
      return;
    }

    if (editDialog.configId) {
      // Update existing config
      updateRestrictionConfigMutation.mutate({
        id: editDialog.configId,
        restrictionConfig,
      });
    } else {
      // Create new config
      createRestrictionConfigMutation.mutate({
        organizationId: currentOrganizationId,
        ehrSystem: selectedEhrSystem,
        labelPattern: editDialog.labelPattern,
        restrictionConfig,
      });
    }
  };

  const handleCloseDialog = () => {
    setEditDialog({ isOpen: false, labelPattern: '', configId: null, currentConfig: null });
  };

  // Filter and sort labels
  const filteredLabels =
    allLabelsQuery.data?.labels.filter((label) => {
      // Text search filter
      if (searchQuery && !label.labelPattern.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Status filter
      if (filterStatus === 'unknown') {
        return !label.hasConfig;
      }
      if (filterStatus === 'configured') {
        return label.hasConfig;
      }
      return true;
    }) || [];

  return (
    <AdminPageLayout
      title="Restriction Configurations"
      description="Manage appointment slot restrictions and label mappings for organizations"
      icon={Database}
      isSuperAdminPage={true}
    >
      <div className="flex flex-col gap-6">
        {/* Organization & EHR System Info */}
        <Card>
          <CardHeader>
            <CardTitle>Organization & EHR System</CardTitle>
            <CardDescription>
              Manage restriction configurations for the current organization
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            <div className="flex-1">
              <div className="text-sm font-medium text-muted-foreground mb-1">Organization</div>
              <div className="text-lg font-semibold">
                {organization?.name ?? 'Unknown Organization'}
              </div>
            </div>
            <div className="w-48">
              <div className="text-sm font-medium text-muted-foreground mb-1">EHR System</div>
              <Select
                value={selectedEhrSystem}
                onValueChange={(value) => setSelectedEhrSystem(value as 'imedicware')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="imedicware">iMedicWare</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        {currentOrganizationId ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Appointment Slot Labels</CardTitle>
                  <CardDescription>
                    Configure restrictions for appointment slot labels found in the EHR
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {allLabelsQuery.data && (
                    <div className="flex gap-2 text-xs">
                      <Badge variant="secondary" className="gap-1">
                        Total: {allLabelsQuery.data.totalCount}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="gap-1 border-green-500/50 text-green-700 bg-green-50"
                      >
                        Configured: {allLabelsQuery.data.configuredCount}
                      </Badge>
                      <Badge variant="destructive" className="gap-1">
                        Unknown: {allLabelsQuery.data.unknownCount}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters and Search */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search label patterns..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="w-full sm:w-[200px]">
                  <Select
                    value={filterStatus}
                    onValueChange={(value) =>
                      setFilterStatus(value as 'all' | 'unknown' | 'configured')
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Labels</SelectItem>
                      <SelectItem value="unknown">Unknown (Action Needed)</SelectItem>
                      <SelectItem value="configured">Configured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {allLabelsQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Loading labels...</span>
                </div>
              ) : allLabelsQuery.error ? (
                <div className="flex items-center justify-center py-8 text-destructive">
                  <AlertTriangle className="h-6 w-6 mr-2" />
                  <span>Error loading labels: {allLabelsQuery.error.message}</span>
                </div>
              ) : filteredLabels.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground border rounded-lg border-dashed">
                  <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No labels found</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLabels.map((label) => (
                    <div
                      key={label.labelPattern}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 rounded-full shrink-0 ${
                            label.hasConfig ? 'bg-green-500' : 'bg-red-500'
                          }`}
                        />
                        <div className="space-y-1">
                          <div className="font-medium flex items-center gap-2">
                            {label.labelPattern}
                            {label.isAutoCreated && (
                              <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                                Auto-detected
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>
                              {label.count} slot{label.count !== 1 ? 's' : ''}
                            </span>
                            <span>•</span>
                            <span
                              className={
                                label.hasConfig ? 'text-green-600 font-medium' : 'text-red-600'
                              }
                            >
                              {label.hasConfig ? 'Configured' : 'Unknown / Not Configured'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant={label.hasConfig ? 'outline' : 'default'}
                        size="sm"
                        onClick={() =>
                          handleEditConfig(
                            label.labelPattern,
                            label.configId || null,
                            label.restrictionConfig || null
                          )
                        }
                      >
                        {label.hasConfig ? 'Edit Configuration' : 'Configure Restrictions'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Database className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Organization</h3>
              <p className="text-muted-foreground text-center max-w-md">
                You must be part of an organization to manage restriction configurations.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Restriction Config Dialog */}
      <EditRestrictionConfigDialog
        isOpen={editDialog.isOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveConfig}
        labelPattern={editDialog.labelPattern}
        currentConfig={editDialog.currentConfig}
        appointmentTypes={appointmentTypesQuery.data?.appointmentTypes || []}
        conditionTags={conditionTagsQuery.data || []}
        isLoading={
          updateRestrictionConfigMutation.isPending ||
          createRestrictionConfigMutation.isPending ||
          allLabelsQuery.isRefetching
        }
      />
    </AdminPageLayout>
  );
}
